/** Global records share the header field with the current page's filtering. */
import { useState } from 'react';
import { Building2, CheckSquare } from '@/shared/ui/icons';
import { useGlobalHeaderSearch } from '../../hooks/useGlobalHeaderSearch';
import type { GlobalSearchType } from '../../services/globalSearch';
import { DesktopHeaderSearch, type DesktopHeaderSearchProps } from './DesktopHeaderSearch';

const groups: Record<GlobalSearchType, string> = { project: 'Projects', task: 'Tasks' };
const icons = { project: Building2, task: CheckSquare };
interface Props extends Omit<DesktopHeaderSearchProps, 'results' | 'globalSearch' | 'resultsLoading' | 'resultsMessage' | 'moreResults' | 'onOpenChange'> {
  actorId?: string | null;
  workspaceId?: string | null;
  onNavigateToUrl?: (url: string) => void;
}
const navigateToUrl = (url: string) => window.location.assign(url);

export function GlobalDesktopHeaderSearch({ actorId, workspaceId, onNavigateToUrl = navigateToUrl, ...props }: Props) {
  const [openedScope, setOpenedScope] = useState<string | null>(null);
  const search = useGlobalHeaderSearch({ actorId, workspaceId, query: props.value, enabled: openedScope === props.scopeKey });
  return <DesktopHeaderSearch {...props} globalSearch
    onOpenChange={open => setOpenedScope(open ? props.scopeKey : null)}
    resultsLoading={search.loading}
    resultsMessage={search.error ?? ''}
    results={(Object.keys(groups) as GlobalSearchType[]).flatMap(type => search.results.filter(result => result.type === type)).map(result => {
      const Icon = icons[result.type];
      return { id: `${result.type}:${result.id}`, label: result.label, description: result.description, groupLabel: groups[result.type],
        icon: <Icon className="h-4 w-4" />, onSelect: async () => {
          const selected = await search.select(result);
          if (!selected) return false;
          onNavigateToUrl(selected.url);
          return true;
        } };
    })}
    moreResults={(Object.keys(groups) as GlobalSearchType[]).filter(type => search.hasMore[type]).map(type => ({
      id: type, label: `More ${groups[type].toLocaleLowerCase()}`, onSelect: () => search.loadMore(type),
    }))}
  />;
}
